/**
 * Si la barra lateral está abierta o recogida, y qué secciones están plegadas.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ SE PUEDE RECOGER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La barra ocupa 17 rem — casi 280 píxeles — **siempre**, y en una pantalla de
 * portátil eso es una quinta parte del ancho. Se lo come una lista que casi
 * todo el tiempo no se está mirando: quien está en el catálogo cargando
 * productos no necesita ver «Caja», «Fiado» y «Monedas» mientras escribe.
 *
 * Recogida deja los iconos, que siguen siendo suficientes para moverse —
 * después de dos días nadie lee esas etiquetas, las reconoce— y devuelve 200
 * píxeles a la tabla que sí se está mirando.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ Y POR QUÉ SE GUARDA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Porque no es una preferencia de un momento: es cómo trabaja esa persona. Si
 * se olvidara al recargar, el que la recoge tendría que recogerla otra vez
 * cada mañana — y a la tercera vez deja de usarla y volvemos al principio.
 */

import { create } from "zustand";

const LLAVE = "softfi-barra";

type Guardado = { colapsada: boolean; cerrados: string[] };

function leer(): Guardado {
  try {
    const crudo = localStorage.getItem(LLAVE);
    if (!crudo) return { colapsada: false, cerrados: [] };
    const datos = JSON.parse(crudo);
    return {
      colapsada: Boolean(datos?.colapsada),
      cerrados: Array.isArray(datos?.cerrados) ? datos.cerrados.filter((x: unknown) => typeof x === "string") : [],
    };
  } catch {
    return { colapsada: false, cerrados: [] };
  }
}

function guardar(estado: Guardado): void {
  try {
    localStorage.setItem(LLAVE, JSON.stringify(estado));
  } catch {
    /* Modo privado o sin espacio: la barra funciona igual, solo se olvida. */
  }
}

type EstadoBarra = Guardado & {
  alternar: () => void;
  alternarGrupo: (titulo: string) => void;
  /** ¿Está plegada esta sección? */
  cerrado: (titulo: string) => boolean;
};

export const useBarra = create<EstadoBarra>((set, get) => ({
  ...leer(),

  alternar: () => {
    const siguiente = { colapsada: !get().colapsada, cerrados: get().cerrados };
    guardar(siguiente);
    set(siguiente);
  },

  alternarGrupo: (titulo) => {
    const actuales = get().cerrados;
    const cerrados = actuales.includes(titulo)
      ? actuales.filter((t) => t !== titulo)
      : [...actuales, titulo];
    const siguiente = { colapsada: get().colapsada, cerrados };
    guardar(siguiente);
    set(siguiente);
  },

  cerrado: (titulo) => get().cerrados.includes(titulo),
}));
